import React, { useState } from "react";
import { Footer, Header } from "../../shared/Layouts/LandingPageLayout";
import { BACKGROUND, CHEVRON_DOWN, INFO } from "../../assets";
import { useQuery } from "react-query";
import {
  getLocalGovernment,
  getStates,
  guestSearch,
  updateApplicationPayment,
  verifyPayment,
} from "../../Services/services";
import { useInterswitchPay } from "../../hooks/useInterswitchPay";
import useDropdown from "../../utils-func/functions/usedropdown";
import toast from "react-hot-toast";
import ButtonV2 from "../../shared/components/buttonV2";
import { useAppDispatch } from "../../hooks";

import { setToken, setUser } from "../../states/slices/authReducer";
import { Tooltip } from "@mui/material";
import { propertyNames } from "../../shared/components/caseformModal";
import PaymentGatewayModal from "../../shared/components/paymentGatewayModal";

function Search() {
  const { dropdownRef, setDropdowns, dropdowns, closeDropDown } = useDropdown();
  const { data: states } = useQuery("states", getStates, {
    refetchOnWindowFocus: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const { data: lga } = useQuery(["lga", currentPage], () =>
    getLocalGovernment(currentPage),
  );
  const dispatch = useAppDispatch();
  const { pay: openInterswitchModal } = useInterswitchPay();
  const [paymentId, setPaymentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedLga, setSelectedLga] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedState, setSelectedState] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [formField, setFormField] = useState({
    name: "",
    email: "",
    title_type: "",
    registered_title_number: "",
    plot_number: "",
    survey_plan_number: "",
    name_of_owner: "",
    street: "",
    city: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;

    setFormField((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGuestSearch = async () => {
    if (
      !formField.name ||
      !formField.email ||
      !selectedUserType ||
      !selectedProperty?.name ||
      !selectedLga?.id ||
      !states?.[0]?.id
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);

    const payload = {
      name: formField.name,
      email: formField.email,
      type: selectedUserType,
      title_type: selectedProperty?.name || "",
      // payment_method: gateway.provider,
      lga_id: selectedLga?.id.toString(),
      state_id: states?.[0]?.id?.toString(),
      city: formField.city,
      street: formField.street,
      // callback_url: `${window.location.origin}/individual/search-result`,
    };

    try {
      const res = await guestSearch(payload);
      if (res) {
        toast.success(res?.message);
        setFormField({
          name: "",
          email: "",
          title_type: "",
          registered_title_number: "",
          plot_number: "",
          survey_plan_number: "",
          name_of_owner: "",
          city: "",
          street: "",
        });
        setPaymentId(res?.data?.payment?.payment_id);
        dispatch(setUser(res?.data?.user));
        dispatch(setToken(res?.data?.token));
        setIsPaymentModalOpen(true);
        // window.location.href = res?.data?.payment?.url;
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPaymentMethod = async (id: number | string) => {
    if (!id) {
      toast.error("payment id is required");
      return;
    }

    setIsSubmittingPayment(true);
    try {
      const response = await updateApplicationPayment(paymentId!, {
        callback_url: `${window.location.origin}/individual/search-result`,
        payment_gateway_id: id.toString(),
      });

      const url: string = response?.data?.url;
      if (!url) return;

      setIsPaymentModalOpen(false);

      if (url.includes("interswitchng.com")) {
        // Interswitch — use inline modal (no redirect)
        openInterswitchModal({
          checkoutUrl: url,
          amountInNaira: response.data.amount,
          onSuccess: async (txnRef: string) => {
            try {
              window.location.href = `${window.location.origin}/individual/search-result?reference=${encodeURIComponent(txnRef)}`;
              // await verifyPayment({ reference: txnRef });
            } catch (e) {
              // verification handled server-side; proceed to result page
            }
          },
          onError: () => {
            toast.error("Payment was not completed. Please try again.");
          },
        });
      } else {
        // Paystack / Moniepoint / others — use normal redirect
        window.location.href = url;
      }
    } catch (error) {
      toast.error("Failed to update payment method. Please try again.");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  return (
    <>
      <Header />
      <div
        className="w-full h-full  flex items-center justify-center mt-24 py-20 md:py-36 relative"
        style={{
          backgroundImage: `url(${BACKGROUND})`,
          backgroundSize: "cover",
        }}
      >
        <span className="text-3xl md:text-[40px] font-bold text-primary-300">
          Search Form
        </span>
        <div className="bg-lightGold bg-opacity-10 w-full h-full absolute"></div>
      </div>
      <div className="mx-auto mt-4 flex max-w-3xl items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
        <img src={INFO} alt="info" className="h-5 w-5 shrink-0 text-blue-600" />
        <p className="text-sm leading-6 text-blue-900">
          Performing a search automatically creates an account, and your login
          credentials will be sent to the email provided. For subsequent
          searches, you are required to log in to your dashboard.
        </p>
      </div>
      <div className="w-full h-full flex items-center py-10 lg:px-56 px-5 md:py-20 md:px-56 justify-center">
        <div className="w-full shadow-container flex p-5 md:p-10 flex-col gap-y-7  rounded-xl">
          <span className="text-2xl text-center font-semibold text-black">
            Fill the search form
          </span>

          <div className="flex flex-col gap-y-4 w-full lg:p-4 p-2">
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-y-4 md:gap-y-0 space-x-0 md:space-x-3">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Name <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  name="name"
                  value={formField.name}
                  onChange={handleInputChange}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Name (Your Full Name)"
                />
              </div>
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Email <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  name="email"
                  value={formField.email}
                  onChange={handleInputChange}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Enter your email"
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-y-4 md:gap-y-0 space-x-0 md:space-x-3">
              <div className="w-full flex flex-col gap-y-2 relative">
                <span className="text-sm font-medium text-[#4F5762]">
                  Type <span className="text-red-500">*</span>
                </span>
                <div
                  onClick={() => setDropdowns({ ...dropdowns, userType: true })}
                  className="flex items-center justify-between  gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                >
                  <input
                    type="text"
                    placeholder="Enter user type"
                    value={selectedUserType}
                    className="w-full bg-transparent outline-none placeholder:font-normal placeholder:text-sm placeholder:text-[#808080]"
                  />
                  {/* <span className="text-sm font-medium text-customDark">
                                          {selectedState?.name || "Enter state"}
                                        </span> */}
                  <img
                    src={CHEVRON_DOWN}
                    className="w-[20px] h-[20px]"
                    alt=""
                  />
                </div>
                {dropdowns.userType && (
                  <div
                    ref={dropdownRef.userType}
                    className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                  >
                    {["individual", "company"].map((item) => (
                      <span
                        key={item}
                        onClick={() => {
                          setSelectedUserType(item);
                          closeDropDown("userType");
                        }}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-full flex flex-col gap-y-2 relative">
                <span className="text-sm font-medium text-[#4F5762]">
                  Type of Property Title <span className="text-red-500">*</span>
                </span>
                <div
                  onClick={() => setDropdowns({ ...dropdowns, property: true })}
                  className="flex items-center justify-between  gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                >
                  <input
                    type="text"
                    placeholder="Enter property title"
                    value={selectedProperty?.name}
                    readOnly
                    className="w-full bg-transparent outline-none placeholder:font-normal placeholder:text-sm placeholder:text-[#808080]"
                  />
                  {/* <span className="text-sm font-medium text-customDark">
                                          {selectedState?.name || "Enter state"}
                                        </span> */}
                  <img
                    src={CHEVRON_DOWN}
                    className="w-[20px] h-[20px]"
                    alt=""
                  />
                </div>
                {propertyNames.length > 0 && dropdowns.property && (
                  <div
                    ref={dropdownRef.property}
                    className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                  >
                    {propertyNames.map((item) => (
                      <span
                        key={item.id}
                        onClick={() => {
                          setSelectedProperty(item);
                          closeDropDown("property");
                        }}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                      >
                        {item.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-y-4 md:gap-y-0 space-x-0 md:space-x-3">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Registered Title Number
                </span>
                <input
                  type="text"
                  name="registered_title_number"
                  onChange={handleInputChange}
                  value={formField.registered_title_number}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Enter registered title number"
                />
              </div>
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Plot Number
                </span>
                <input
                  type="text"
                  name="plot_number"
                  value={formField.plot_number}
                  onChange={handleInputChange}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Enter plot number"
                />
              </div>
            </div>
            <div className="w-full flex flex-col md:flex-row items-center gap-y-4 md:gap-y-0 md:gap-x-2">
              <div className="w-full flex flex-col gap-y-2">
                <span className="text-sm font-medium text-[#4F5762]">
                  City/Town
                </span>
                <input
                  type="text"
                  name="city"
                  value={formField.city}
                  onChange={handleInputChange}
                  placeholder="Enter city/town"
                  className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                />
              </div>
              <div className="w-full flex flex-col gap-y-2">
                <span className="text-sm font-medium text-[#4F5762]">
                  Street Name
                </span>
                <input
                  type="text"
                  name="street"
                  value={formField.street}
                  onChange={handleInputChange}
                  placeholder="Enter street name"
                  className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-y-4 md:gap-y-0 space-x-0 md:space-x-3">
              <div className="w-full flex flex-col gap-y-2 relative">
                <span className="text-sm font-medium text-[#4F5762]">
                  States <span className="text-red-500">*</span>
                </span>
                <div
                  onClick={() => setDropdowns({ ...dropdowns, state: true })}
                  className="flex items-center justify-between  gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                >
                  <input
                    type="text"
                    placeholder="Enter state"
                    value={selectedState?.name}
                    className="w-full bg-transparent outline-none placeholder:font-normal placeholder:text-sm placeholder:text-[#808080]"
                  />
                  {/* <span className="text-sm font-medium text-customDark">
                                          {selectedState?.name || "Enter state"}
                                        </span> */}
                  <img
                    src={CHEVRON_DOWN}
                    className="w-[20px] h-[20px]"
                    alt=""
                  />
                </div>
                {states?.length > 0 && dropdowns.state && (
                  <div
                    ref={dropdownRef.state}
                    className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                  >
                    {states?.map((item: { id: number; name: string }) => (
                      <span
                        key={item.id}
                        onClick={() => {
                          setSelectedState(item);
                          closeDropDown("state");
                        }}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                      >
                        {item.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-full flex flex-col gap-y-2 relative">
                <span className="text-sm font-medium text-[#4F5762]">
                  Local Government Area (LGA){" "}
                  <span className="text-red-500">*</span>
                </span>
                <div
                  onClick={() => {
                    if (!selectedState) {
                      toast.error("Select a state");
                      return;
                    }
                    setDropdowns({ ...dropdowns, lga: true });
                  }}
                  className="flex items-center justify-between gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                >
                  <input
                    type="text"
                    value={selectedLga?.name}
                    placeholder="Select state first"
                    className="w-full bg-transparent outline-none placeholder:font-normal placeholder:text-sm placeholder:text-[#808080]"
                  />
                  {/* <span className="text-sm font-medium text-customDark">
                                        {selectedLga?.name || "Enter local govt area (LGA)"}
                                      </span> */}
                  <img
                    src={CHEVRON_DOWN}
                    className="w-[20px] h-[20px]"
                    alt=""
                  />
                </div>
                {lga?.data?.length > 0 && dropdowns.lga && (
                  <div
                    ref={dropdownRef.lga}
                    className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                  >
                    {lga?.data?.map(
                      (item: {
                        id: number;
                        name: string;
                        state_id: number;
                        created_at: string;
                        updated_at: string;
                      }) => (
                        <span
                          key={item.id}
                          onClick={() => {
                            setSelectedLga(item);
                            closeDropDown("lga");
                          }}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                        >
                          {item.name}
                        </span>
                      ),
                    )}
                    <div className="flex items-center gap-x-2 justify-center">
                      <Tooltip title="Previous">
                        <button
                          onClick={() =>
                            setCurrentPage(lga?.meta?.current_page - 1)
                          }
                          disabled={lga?.meta?.current_page === 1}
                          className={`w-[30px] h-[30px] rotate-180 hover:bg-gray-100 rounded-full flex items-center justify-center ${
                            lga?.meta?.current_page === 1 &&
                            "cursor-not-allowed"
                          }`}
                        >
                          <img src={CHEVRON_DOWN} alt="" />
                        </button>
                      </Tooltip>
                      <Tooltip title="Next">
                        <button
                          onClick={() =>
                            setCurrentPage(lga?.meta?.current_page + 1)
                          }
                          disabled={
                            lga?.meta?.current_page === lga?.meta?.last_page
                          }
                          className={`w-[30px] h-[30px] hover:bg-gray-100 rounded-full flex items-center justify-center ${
                            lga?.meta?.current_page === lga?.meta?.last_page &&
                            "cursor-not-allowed"
                          }`}
                        >
                          <img src={CHEVRON_DOWN} alt="" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-y-4 md:gap-y-0 space-x-0 md:space-x-3">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Survey Plan Number
                </span>
                <input
                  type="text"
                  name="survey_plan_number"
                  onChange={handleInputChange}
                  value={formField.survey_plan_number}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Enter your survey plan number"
                />
              </div>
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-sm font-medium text-[#4F5762]">
                  Name of Owner of Property (Optional)
                </span>
                <input
                  type="text"
                  name="name_of_owner"
                  value={formField.name_of_owner}
                  onChange={handleInputChange}
                  className="w-full border border-borderColor placeholder:font-normal placeholder:text-sm placeholder:text-[#808080] rounded-xl shadow-box outline-none bg-transparent p-3"
                  placeholder="Enter name of owner of property (Optional)"
                />
              </div>
            </div>
          </div>

          <ButtonV2
            title="Search"
            loading={loading}
            loaderColor="#ffffff"
            textStyle="text-white font-medium text-[16px]"
            handleClick={handleGuestSearch}
            btnStyle="flex self-end px-5 py-3 rounded-full bg-globalGold text-white font-medium text-[16px]"
          />
        </div>
        <PaymentGatewayModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onSelect={handleSelectPaymentMethod}
          isSubmitting={isSubmittingPayment}
        />
      </div>
      <Footer />
    </>
  );
}

export default Search;
